import { useState, useEffect, useRef, useCallback } from "react";

const FALLBACK_REVIEWS = [
  {
    author: "Husejer på Fyn",
    location: "Svendborg omegn",
    service: "Tagarbejde",
    rating: 5,
    text: "God dialog gennem hele forløbet og et veludført stykke arbejde med taget. Aftaler blev overholdt, og byggepladsen blev efterladt pæn og ryddelig.",
    is_fictional: true,
  },
  {
    author: "Familie i Skårup",
    location: "Skårup Fyn",
    service: "Døre og vinduer",
    rating: 5,
    text: "Vi fik udskiftet vinduer og facadedør. Man mærker tydeligt, at Andreas elsker at lave døre og vinduer – montagen og de afsluttende lister sidder fuldstændig lige i skabet!",
    is_fictional: true,
  },
  {
    author: "Ejendomsejer",
    location: "Sydfyn",
    service: "Træterrasser & udendørsarealer",
    rating: 5,
    text: "Vi fik opført en fantastisk ny træterrasse i haven. Super finish på samlinger og trapper, og Andreas rådgav grundigt om holdbare træsorter.",
    is_fictional: true,
  },
  {
    author: "Husejer på Fyn",
    location: "Svendborg omegn",
    service: "Indendørs renovering",
    rating: 5,
    text: "God dialog gennem hele renoveringen af vores lofter og indvendige vægge. Nye lofter og finish står knivskarpt, og byggepladsen blev efterladt ren og pæn.",
    is_fictional: true,
  },
];

export default function ReviewsSlider() {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Fetch reviews from CMS
  useEffect(() => {
    let ignore = false;
    async function loadReviews() {
      try {
        const res = await fetch("/_api/public/content/reviews");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0 && !ignore) {
            setReviews(data);
          }
        }
      } catch (err) {
        // use fallback silently
      }
    }
    loadReviews();
    return () => {
      ignore = true;
    };
  }, []);

  const total = reviews.length;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Autoplay / infinite rotation respecting reduced motion & hover pause
  useEffect(() => {
    // Check prefers-reduced-motion
    if (typeof window !== "undefined") {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mediaQuery.matches) return;
    }

    if (isPaused || total <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, total, nextSlide]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 40) {
      nextSlide();
    } else if (diff < -40) {
      prevSlide();
    }
  };

  // Visible window of reviews: create circular array of 3 items
  const visibleItems = [];
  for (let i = 0; i < Math.min(3, total); i++) {
    const idx = (currentIndex + i) % total;
    visibleItems.push({ review: reviews[idx], key: `${idx}-${i}` });
  }

  return (
    <section
      className="py-16 sm:py-24 bg-[#FAFAF8] border-t border-b border-[#E5E5E0] relative overflow-hidden"
      aria-label="Kundeudtalelser og anmeldelser"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-2">
              <span className="w-6 h-0.5 bg-[#A78E51]"></span>
              <span>Anmeldelser & erfaringer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111111] tracking-tight">
              Troværdigt håndværk og tilfredse kunder
            </h2>
            <p className="mt-3 text-base text-neutral-600">
              Vi lægger vægt på ordentlig dialog, pålidelige aftaler og høj faglig stolthed i enhver tømreropgave.
            </p>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={prevSlide}
              aria-label="Forrige anmeldelse"
              className="w-11 h-11 rounded-full border border-neutral-300 bg-white text-[#111111] hover:border-[#A78E51] hover:text-[#A78E51] transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#A78E51] shadow-sm"
            >
              <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={nextSlide}
              aria-label="Næste anmeldelse"
              className="w-11 h-11 rounded-full border border-neutral-300 bg-white text-[#111111] hover:border-[#A78E51] hover:text-[#A78E51] transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#A78E51] shadow-sm"
            >
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Reviews Cards: 3 on desktop, 2 on tablet, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleItems.map(({ review, key }, index) => {
            // responsive visibility:
            // index 0: always visible
            // index 1: md:block
            // index 2: lg:block
            const visibilityClass =
              index === 0
                ? "block"
                : index === 1
                ? "hidden md:block"
                : "hidden lg:block";

            return (
              <div
                key={key}
                className={`${visibilityClass} bg-white rounded-lg border border-neutral-200 p-6 sm:p-7 shadow-sm flex flex-col justify-between transition-all relative`}
              >
                {/* Mandatory Disclaimer Badge */}
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-amber-50 text-amber-900 border border-amber-200 text-xs font-semibold">
                    <i className="fa-solid fa-circle-info text-amber-700 text-[11px]" aria-hidden="true" />
                    Eksempelanmeldelse – fiktiv
                  </span>
                </div>

                {/* Rating stars */}
                <div className="flex items-center gap-1 text-[#A78E51] text-sm mb-3">
                  {[...Array(5)].map((_, starIndex) => (
                    <i
                      key={starIndex}
                      className={`fa-solid fa-star ${
                        starIndex < (review.rating || 5)
                          ? "text-[#A78E51]"
                          : "text-neutral-200"
                      }`}
                      aria-hidden="true"
                    />
                  ))}
                  <span className="text-xs text-neutral-500 font-medium ml-2">
                    5.0
                  </span>
                </div>

                {/* Quote text */}
                <p className="text-neutral-700 text-sm sm:text-base leading-relaxed italic flex-1 mb-6">
                  &ldquo;{review.text}&rdquo;
                </p>

                {/* Author and service details */}
                <div className="pt-4 border-t border-neutral-100 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[#111111] text-sm">
                      {review.author}
                    </div>
                    {review.location && (
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <i className="fa-solid fa-location-dot text-[#A78E51] text-[10px]" aria-hidden="true" />
                        <span>{review.location}</span>
                      </div>
                    )}
                  </div>
                  {review.service && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-neutral-100 text-neutral-700">
                      {review.service}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Slider indicator dots */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {reviews.map((_, dotIndex) => (
            <button
              key={dotIndex}
              type="button"
              onClick={() => setCurrentIndex(dotIndex)}
              aria-label={`Gå til anmeldelse ${dotIndex + 1}`}
              className={`h-2 rounded-full transition-all ${
                currentIndex === dotIndex
                  ? "w-8 bg-[#A78E51]"
                  : "w-2 bg-neutral-300 hover:bg-neutral-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
